'use client';

import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';

import BaseButton, { type BaseButtonProps } from '@/app/_components/base/button';
import { cn } from '@/app/lib/utils/tailwindcss';

const buttonVariants = cva(
  // Base styles
  [
    'flex h-11 w-full items-center justify-center rounded-sm shrink-0 cursor-pointer',
    'typo-body-4',
    'disabled:cursor-not-allowed',
  ],
  {
    variants: {
      variant: {
        primary: [
          'bg-button-primary-default-bg text-button-primary-default-text',
          'disabled:bg-button-primary-disabled-bg disabled:text-button-primary-disabled-text',
        ],
        submit: [
          'bg-button-submit-default-bg rounded-lg text-button-submit-default-text',
          'disabled:opacity-40',
        ],
        code: [
          'gap-2 bg-button-code-default-bg rounded-lg text-button-code-default-text', // Override rounded
          'disabled:opacity-40',
        ],
        secondary: [
          'h-8 w-auto px-4 py-2', // Override base h-11, w-full
          'bg-button-secondary-default-bg text-button-secondary-default-text',
          'disabled:bg-button-secondary-disabled-bg disabled:text-button-secondary-disabled-text',
        ],
        secondaryCircle: [
          'h-8 rounded-3xl', // Override base h-11, rounded
          'bg-button-secondary-circle-default-bg text-button-secondary-circle-default-text',
          'disabled:bg-button-secondary-circle-disabled-bg disabled:text-button-secondary-circle-disabled-text',
        ],
        modalSecondary: [
          'bg-button-modal-secondary-default-bg text-button-modal-secondary-default-text',
          'disabled:bg-button-modal-secondary-disabled-bg disabled:text-button-modal-secondary-disabled-text',
        ],
        purchase: [
          'typo-heading-5 rounded', // base typo-body-4, rounded-sm override
          'bg-button-purchase-default-bg text-button-purchase-default-text',
          'disabled:bg-button-purchase-disabled-bg disabled:text-button-purchase-disabled-text',
        ],
        ghost: [
          'border border-g-00 bg-transparent text-g-00',
          'disabled:border-g-00/20 disabled:bg-transparent disabled:text-g-00/20',
        ],
      },
    },
    defaultVariants: {
      variant: 'submit',
    },
  },
);

export type ButtonProps = BaseButtonProps & VariantProps<typeof buttonVariants>;

export default function Button({ variant, className, ...props }: ButtonProps) {
  return <BaseButton className={cn(buttonVariants({ variant }), className)} {...props} />;
}

export { buttonVariants };
